// Shared theme configuration for Sequins UI
// This file contains both light and dark theme configurations

// Brand colors
const brandColors = {
  primary: '#000', // Black: fresh, clean, biotech feel
  secondary: '#1E4CB0', // Sequins Blue: vibrant and trustworthy
}

// Base theme properties shared across light and dark themes
const commonBase = {
  // Typography
  fontSizeMedium: '14px',
  fontFamily:
    'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',

  // Status colors with good contrast (work well in both themes)
  successColor: '#059669',
  warningColor: '#D97706',
  errorColor: '#DC2626',
  infoColor: '#2563EB',

  // Naive UI global theme overrides (map to CSS variables)
  primaryColor: brandColors.primary, // --n-color
  primaryColorDisabled: brandColors.primary, // --n-color-disabled
  rippleColor: brandColors.secondary, // --n-ripple-color
}

// Light theme configuration
export const lightThemeOverride = {
  common: {
    ...commonBase,
    primaryColor: brandColors.primary,
    bodyColor: 'rgba(230, 230, 230, 1)',
    scrollbarColor: 'rgba(230, 230, 230, 1)',
  },

  Button: {
    // Buttons: refined for comfortable, high-contrast white/black aesthetic
    // Use subtle fills for default buttons and solid black for primary buttons.
    // Add soft shadows and a clear focus ring using the brand accent (Sequins Blue).
    color: 'rgba(0, 0, 0, 0.02)', // very subtle default fill to retain shape on light bg
    colorHover: 'rgba(0, 0, 0, 0.06)',
    colorPressed: 'rgba(0, 0, 0, 0.10)',
    colorFocus: 'rgba(0, 0, 0, 0.08)',

    // Primary (call-to-action) buttons: solid black with white text
    colorPrimary: '#000000',
    colorHoverPrimary: '#050505',
    colorPressedPrimary: `${brandColors.secondary}33`,
    // Use the brand blue as an accessible focus ring around black primary buttons
    colorFocusPrimary: '#0a0a0a',

    // Text colors (kept simple: black on light, white on primary)
    textColor: '#000000',
    textColorHover: '#000000',
    textColorPressed: '#000000',
    textColorFocus: '#000000',

    textColorPrimary: '#ffffff',
    textColorHoverPrimary: '#ffffff',
    textColorPressedPrimary: '#ffffff',
    textColorFocusPrimary: '#ffffff',

    // Borders and subtle elevation to maintain button shape and affordance
    border: '1px solid rgba(0, 0, 0, 0.06)',
    borderHover: '1px solid rgba(0, 0, 0, 0.10)',
    borderFocus: '1px solid rgba(0, 0, 0, 0.12)',
    borderPressed: '1px solid rgba(0, 0, 0, 0.14)',

    borderPrimary: '1px solid rgba(0, 0, 0, 0.06)',
    borderHoverPrimary: '1px solid rgba(0, 0, 0, 0.10)',
    borderFocusPrimary: '1px solid rgba(0, 0, 0, 0.12)',
    borderPressedPrimary: `1px solid ${brandColors.secondary}33`,

    // Softer, bolder typography for buttons
    fontWeight: '600',
  },

  Input: {
    borderHover: `1px solid ${brandColors.secondary}`,
    borderFocus: `1px solid ${brandColors.secondary}`,
  },

  // Naive UI Menu theme overrides — use the library's expected properties and valid CSS colors
  Menu: {
    // subtle light-gray hover and a slightly stronger active highlight
    itemColorHover: 'rgba(0, 0, 0, 0.12)',
    itemColorActive: 'rgba(0, 0, 0, 0.06)',
    // optional text color adjustments for better contrast on light background
    itemTextColor: brandColors.primary,
    itemTextColorActive: brandColors.primary,
  },
}

const darkBgColor = '#181d25ff'

// Dark theme configuration
export const darkThemeOverride = {
  common: {
    ...commonBase,
    backgroundColor: darkBgColor, // deeper dark background
    bodyColor: darkBgColor,

    primaryColor: brandColors.secondary, // #3A86FF
    primaryColorHover: '#5CAEFF', // lighter blue for hover
    primaryColorPressed: '#265ECF', // darker blue for pressed
    primaryColorFocus: '#5CAEFF', // match hover for focus
    primaryColorDisabled: brandColors.secondary, // keep secondary for disabled
    rippleColor: brandColors.secondary,
    scrollbarColor: 'rgba(58, 134, 255, 0.15)', // subtle blue tint
  },

  Button: {
    textColorPrimary: '#E3F0FF', // light blue text for contrast
    textColorHoverPrimary: '#FFFFFF',
    textColorPressedPrimary: '#B3D1FF',
    textColorFocusPrimary: '#FFFFFF',
  },

  Input: {
    borderHover: `1px solid ${brandColors.secondary}`,
    borderFocus: `1px solid ${brandColors.secondary}`,
    color: '#E3F0FF',
    placeholderColor: '#7BAAF7',
    backgroundColor: '#232A36',
  },

  Menu: {
    itemTextColor: '#FFFFFF',
    itemTextColorActive: '#FFFFFF',

    itemColorHover: 'rgba(11, 11, 11, 0.90)',
    itemColorActive: 'rgba(11, 11, 11, 0.80)',
  },
}

// Theme configuration object for easy access
export const themeOverrides = {
  light: lightThemeOverride,
  dark: darkThemeOverride,
}
